import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle2 } from 'lucide-react';
import { ShareUtility } from '../components/ShareUtility';
import { SystemSeismograph } from '../components/SystemSeismograph';
export function SnapshotOutput() {
  const {
    reportId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const state = searchParams.get('state') || 'preview';
  const isUnlocked = state === 'full';
  // Mock user data (in production, this would come from API/state)
  const userData = {
    name: 'Anish',
    dateOfBirth: '19 June 1989',
    timeOfBirth: '2:50 PM',
    placeOfBirth: 'Surat, India'
  };
  // Self system data - "Understanding is control"
  // High cognitive activity (exploration), dips into deep analysis, spikes in control
  const systemPatternData = [{
    x: 0,
    y: 30
  }, {
    x: 20,
    y: 70
  }, {
    x: 40,
    y: 50
  }, {
    x: 60,
    y: 80
  }, {
    x: 80,
    y: 40
  }, {
    x: 100,
    y: 20
  }];
  // Preview content (always visible) - Sections 1-3
  const previewContent = {
    section1: {
      title: '1. Core Legacy Formation',
      subtitle: 'Understanding is control.',
      content: `This system formed around a central rule:

"If I understand how things work deeply enough, I won't be trapped inside bad systems, bad decisions, or other people's incompetence."

This is not emotional sensitivity.

This is cognitive-control wiring.

Early environment rewarded:
• thinking ahead
• seeing second-order consequences
• spotting structural flaws
• not relying on fragile or poorly designed systems

So this system optimized for:

Understanding · Anticipation · Abstraction · Structural control

It does not seek comfort.

It seeks coherence.`
    },
    section2: {
      title: '2. Inherited Axis',
      subtitle: 'Autonomy × Control',
      content: `Two deep instructions run in parallel:

• Dependence = vulnerability
• If I don't design the structure, I'll be stuck inside someone else's bad design

This creates a system that:

• resists being boxed into predefined roles
• has low tolerance for sloppy thinking or fuzzy authority
• prefers building frameworks over following processes
• keeps mentally re-architecting its environment
• feels internally constrained in most "normal" structures

This is not ego.

This is architecture-intolerance.`
    },
    section3: {
      title: '3. Native Intelligence Type',
      subtitle: 'The Architect',
      content: `This system's intelligence is:

• structural
• systems-first
• abstraction-oriented
• pattern-recognizing

It naturally:

• sees hidden dependencies
• designs frameworks
• thinks in second-order effects
• gets impatient with surface-level solutions

This mind is built to design systems, not merely operate inside them.`
    }
  };
  // Full report content (visible after unlock) - Sections 4-10
  const fullReportContent = {
    section4: {
      title: '4. Mask vs Core',
      content: `External presentation:

• articulate
• composed
• intellectually confident
• "has thought this through"

Internal reality:

• constant background analysis
• continuous scanning for structural flaws
• impatience with incoherent systems
• pressure to make things make sense at a deep level

The calm is real.

The mental load underneath it is also real.`
    },
    section5: {
      title: '5. System Cost',
      content: `This configuration produces:

• chronic cognitive overactivity
• difficulty fully resting inside imperfect structures
• impatience with slow or politically messy environments
• a tendency to carry more responsibility for coherence than necessary

This system rarely feels finished.

Only temporarily stable.`
    },
    section6: {
      title: '6. System Limits',
      content: `This system struggles with:

• surrendering control to low-quality structures
• trusting vague leadership or fuzzy plans
• staying long in environments that can't be re-architected
• accepting "good enough" as a final state

When structure is missing, this system stays activated.`
    },
    section7: {
      title: '7. Current Life Stage Module',
      subsections: [{
        subtitle: '7.1 System Phase: Re-Architecture Phase (Mid-30s)',
        content: `This is a phase where:

"The old internal and external structures no longer fit, but the new architecture is still under construction."

Typical symptoms:

• dissatisfaction with existing containers
• urge to redesign work, identity, and long-term direction
• intolerance for patchwork or incremental fixes
• desire to build something clean, ownable, and coherent`
      }, {
        subtitle: '7.2 Core Tension',
        content: `The system wants:

Full coherence + full control

Reality is forcing:

Sequencing + partial builds + imperfect containers

This creates:

• impatience
• strategic overthinking
• periodic "burn it and rebuild" impulses`
      }]
    },
    section8: {
      title: '8. North Star Module',
      subsections: [{
        subtitle: '8.1 What the North Star Is',
        content: `Not a goal.

A constraint that organizes decisions.

For this system, the North Star is:

"Building something structurally clean, sovereign, and non-fragile."`
      }, {
        subtitle: '8.2 Orientation Axis',
        content: `This system is oriented toward:

• leverage over effort
• architecture over tactics
• coherence over optimization
• building systems, not just outcomes`
      }, {
        subtitle: '8.3 Work-Structure Fit (Diagnostic)',
        content: `Misaligned with:

• politically messy organizations
• low-agency roles
• maintenance work
• incremental patchwork systems

Aligned with:

• greenfield systems
• platform or OS-level work
• founder or architect roles
• meta-level design problems`
      }, {
        subtitle: '8.4 Core Misalignment Risk',
        content: `If life becomes:

"Maintenance instead of architecture"

This system will:

• lose energy
• become cynical
• start mentally exiting before physically exiting`
      }]
    },
    section9: {
      title: '9. Pattern Mirror',
      content: `Core Truth:
This system uses understanding as a form of control.

Hidden Fear:
Being trapped inside a badly designed life.

Power Leak:
Over-designing before fully committing.

Long-Range Risk:
Endlessly refining the map instead of letting one territory fully compound.`
    },
    section10: {
      title: '10. Final System Summary',
      content: `This is a high-agency architect system that suffers inside incoherent structures and comes alive when building clean ones.

The current tension is not a crisis.

It is a re-architecture phase.`
    }
  };
  const handleUnlock = () => {
    navigate(`/report/${reportId}/unlock`);
  };
  const formatContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('•')) {
        return <div key={i} className="flex items-start gap-3 ml-4" data-id="element-4572">
            <span className="text-[#64748B] mt-1.5" data-id="element-4573">•</span>
            <span data-id="element-4574">{line.substring(1).trim()}</span>
          </div>;
      }
      if (line.trim().startsWith('"') && line.trim().endsWith('"')) {
        return <blockquote key={i} className="border-l-2 border-[#64748B] pl-6 italic text-[#F5F5F5]/90 my-4" data-id="element-4575">
            {line.trim().slice(1, -1)}
          </blockquote>;
      }
      if (line.trim() === '') {
        return <div key={i} className="h-4" data-id="element-4576" />;
      }
      return <p key={i} data-id="element-4577">{line}</p>;
    });
  };
  return <>
      <Helmet data-id="element-4578">
        <title data-id="element-4579">Self System Diagnostic — Report | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4580" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4581">
        {/* Navigation */}
        <header data-id="element-4582">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-4583">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4584">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-4585">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-4586" />
                <span className="text-lg font-medium tracking-tight" data-id="element-4587">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-4588">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-4589">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-4590">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-24 px-6" data-id="element-4591">
          <div className="max-w-4xl mx-auto" data-id="element-4592">
            {/* COVER / CONTEXT */}
            <section className="mb-16" data-id="element-4593">
              <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-4594">
                <div className="flex items-start justify-between mb-6" data-id="element-4595">
                  <h1 className="text-3xl md:text-4xl font-medium leading-tight tracking-tight" data-id="element-4596">
                    🧬 Self System Diagnostic
                  </h1>
                  <div className={`px-3 py-1 border rounded text-xs uppercase tracking-wider flex items-center gap-2 ${isUnlocked ? 'bg-[#64748B]/20 border-[#64748B]/30 text-[#64748B]' : 'bg-[#2B2B2B]/20 border-[#2B2B2B] text-[#F5F5F5]/50'}`} data-id="element-4597">
                    <span data-id="element-4598">{isUnlocked ? '🟢' : '🟡'}</span>
                    {isUnlocked ? 'Full Diagnostic' : 'Preview'}
                  </div>
                </div>
                <div className="space-y-2 text-sm text-[#F5F5F5]/60 mb-6" data-id="element-4599">
                  <p data-id="element-4600">
                    <strong className="text-[#F5F5F5]/80" data-id="element-4601">Name:</strong>{' '}
                    {userData.name}
                  </p>
                  <p data-id="element-4602">
                    <strong className="text-[#F5F5F5]/80" data-id="element-4603">
                      Date of Birth:
                    </strong>{' '}
                    {userData.dateOfBirth}
                  </p>
                  <p data-id="element-4604">
                    <strong className="text-[#F5F5F5]/80" data-id="element-4605">Time:</strong>{' '}
                    {userData.timeOfBirth}
                  </p>
                  <p data-id="element-4606">
                    <strong className="text-[#F5F5F5]/80" data-id="element-4607">Place:</strong>{' '}
                    {userData.placeOfBirth}
                  </p>
                </div>
                <blockquote className="border-l-2 border-[#64748B] pl-6 italic text-[#F5F5F5]/80" data-id="element-4608">
                  This is a structural diagnostic of how this system is
                  configured and how it behaves under load.
                </blockquote>
              </div>

              {/* ZONE 2: Full Report Top Utility Row (only if unlocked) */}
              {isUnlocked && <div className="mt-8" data-id="element-4609">
                  <ShareUtility variant="full-top" reportId={reportId} reportType="self" data-id="element-4610" />
                </div>}
            </section>

            {/* System Map Visualization */}
            <section className="mb-16" data-id="element-4611">
              <div className="mb-6" data-id="element-4612">
                <h2 className="text-xl font-medium mb-2" data-id="element-4613">
                  System Architecture Map
                </h2>
                <p className="text-sm text-[#F5F5F5]/60" data-id="element-4614">
                  Visualizing your system's cognitive control and exploration
                  patterns.
                </p>
              </div>
              <SystemSeismograph data={systemPatternData} label="Self System Pattern" data-id="element-4615" />
            </section>

            {/* PREVIEW LAYER - Sections 1-3 */}
            <section className="mb-16" data-id="element-4616">
              {!isUnlocked && <div className="mb-8 flex items-center gap-3" data-id="element-4617">
                  <div className="px-3 py-1 bg-[#64748B]/20 border border-[#64748B]/30 rounded text-xs text-[#64748B] uppercase tracking-wider" data-id="element-4618">
                    Preview Layer
                  </div>
                </div>}

              {/* Section 1 */}
              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4619">
                <h2 className="text-2xl font-medium mb-3 tracking-tight" data-id="element-4620">
                  {previewContent.section1.title}
                </h2>
                <h3 className="text-lg font-medium mb-6 text-[#F5F5F5]/80" data-id="element-4621">
                  {previewContent.section1.subtitle}
                </h3>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4622">
                  {formatContent(previewContent.section1.content)}
                </div>
              </div>

              {/* Section 2 */}
              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4623">
                <h2 className="text-2xl font-medium mb-3 tracking-tight" data-id="element-4624">
                  {previewContent.section2.title}
                </h2>
                <h3 className="text-lg font-medium mb-6 text-[#F5F5F5]/80" data-id="element-4625">
                  {previewContent.section2.subtitle}
                </h3>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4626">
                  {formatContent(previewContent.section2.content)}
                </div>
              </div>

              {/* Section 3 */}
              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4627">
                <h2 className="text-2xl font-medium mb-3 tracking-tight" data-id="element-4628">
                  {previewContent.section3.title}
                </h2>
                <h3 className="text-lg font-medium mb-6 text-[#F5F5F5]/80" data-id="element-4629">
                  {previewContent.section3.subtitle}
                </h3>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4630">
                  {formatContent(previewContent.section3.content)}
                </div>
              </div>

              {/* Preview End Notice */}
              {!isUnlocked && <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-4631">
                  <p className="text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-4632">
                    🔒 This is the first layer of your system diagnostic.
                  </p>
                  <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-4633">
                    The full report continues with:
                  </p>
                  <ul className="space-y-2 text-[#F5F5F5]/60 text-sm mb-6" data-id="element-4634">
                    <li data-id="element-4635">• The complete internal architecture (Mask vs Core)</li>
                    <li data-id="element-4636">• The real costs and limits of this system</li>
                    <li data-id="element-4637">• Your current life-stage pressure dynamics</li>
                    <li data-id="element-4638">• Your decision and commitment structure</li>
                    <li data-id="element-4639">• Your orientation (North Star) mechanics</li>
                    <li data-id="element-4640">
                      • And the deeper pattern tensions that run this system
                    </li>
                  </ul>
                  <p className="text-[#F5F5F5]/60 text-sm" data-id="element-4641">
                    Below is the rest of the same diagnostic, currently locked.
                  </p>
                </div>}
            </section>

            {/* PAYWALL / UNLOCK SCREEN */}
            {!isUnlocked && <section className="mb-16" data-id="element-4642">
                <div className="border-2 border-[#64748B]/30 p-12 bg-[#111214] text-center" data-id="element-4643">
                  <Lock className="w-12 h-12 text-[#64748B] mx-auto mb-6" data-id="element-4644" />
                  <h2 className="text-3xl font-medium mb-6" data-id="element-4645">
                    Unlock Full Diagnostic
                  </h2>
                  <p className="text-lg text-[#F5F5F5]/70 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-4646">
                    This is not a different report. This is the rest of the same
                    diagnostic.
                  </p>

                  <div className="mb-12" data-id="element-4647">
                    <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-6 uppercase tracking-wider" data-id="element-4648">
                      Locked Sections
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto text-left" data-id="element-4649">
                      {['4. Mask vs Core', '5. System Cost', '6. System Limits', '7. Current Life Stage Module', '8. North Star Module', '9. Pattern Mirror', '10. Final System Summary'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-4650">
                          <Lock className="w-4 h-4 text-[#64748B] flex-shrink-0 mt-0.5" data-id="element-4651" />
                          <span className="text-[#F5F5F5]/70 text-sm" data-id="element-4652">
                            {item}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  <button onClick={handleUnlock} className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-4653">
                    🔓 Unlock Full Diagnostic →
                  </button>

                  <p className="mt-6 text-xs text-[#F5F5F5]/40" data-id="element-4654">
                    One-time unlock · Complete diagnostic access
                  </p>
                </div>

                {/* ZONE 1: Preview Page Share Utility (after hard cutoff) */}
                <ShareUtility variant="preview" reportId={reportId} reportType="self" data-id="element-4655" />
              </section>}

            {/* FULL PAID REPORT - Sections 4-10 */}
            {isUnlocked && <section id="unlocked-content" data-id="element-4656">
                <div className="mb-8 flex items-center gap-3" data-id="element-4657">
                  <div className="px-3 py-1 bg-[#64748B]/20 border border-[#64748B]/30 rounded text-xs text-[#64748B] uppercase tracking-wider" data-id="element-4658">
                    Full Diagnostic
                  </div>
                </div>

                {/* Section 4 */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4659">
                  <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4660">
                    {fullReportContent.section4.title}
                  </h2>
                  <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4661">
                    {formatContent(fullReportContent.section4.content)}
                  </div>
                </div>

                {/* Section 5 */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4662">
                  <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4663">
                    {fullReportContent.section5.title}
                  </h2>
                  <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4664">
                    {formatContent(fullReportContent.section5.content)}
                  </div>
                </div>

                {/* Section 6 */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4665">
                  <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4666">
                    {fullReportContent.section6.title}
                  </h2>
                  <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4667">
                    {formatContent(fullReportContent.section6.content)}
                  </div>
                </div>

                {/* Section 7 - with subsections */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4668">
                  <h2 className="text-2xl font-medium mb-8 tracking-tight" data-id="element-4669">
                    {fullReportContent.section7.title}
                  </h2>
                  {fullReportContent.section7.subsections.map((subsection, i) => <div key={i} className="mb-8" data-id="element-4670">
                        <h3 className="text-lg font-medium mb-4 text-[#F5F5F5]/80" data-id="element-4671">
                          {subsection.subtitle}
                        </h3>
                        <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4672">
                          {formatContent(subsection.content)}
                        </div>
                      </div>)}
                </div>

                {/* Section 8 - with subsections */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4673">
                  <h2 className="text-2xl font-medium mb-8 tracking-tight" data-id="element-4674">
                    {fullReportContent.section8.title}
                  </h2>
                  {fullReportContent.section8.subsections.map((subsection, i) => <div key={i} className="mb-8" data-id="element-4675">
                        <h3 className="text-lg font-medium mb-4 text-[#F5F5F5]/80" data-id="element-4676">
                          {subsection.subtitle}
                        </h3>
                        <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4677">
                          {formatContent(subsection.content)}
                        </div>
                      </div>)}
                </div>

                {/* Section 9 */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4678">
                  <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4679">
                    {fullReportContent.section9.title}
                  </h2>
                  <div className="text-[#F5F5F5]/70 leading-relaxed space-y-4" data-id="element-4680">
                    {formatContent(fullReportContent.section9.content)}
                  </div>
                </div>

                {/* Section 10 */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4681">
                  <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4682">
                    {fullReportContent.section10.title}
                  </h2>
                  <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-4683">
                    {formatContent(fullReportContent.section10.content)}
                  </div>
                </div>

                {/* ZONE 3: Full Report End Share Utility (after final mirror) */}
                <ShareUtility variant="full-end" reportId={reportId} reportType="self" data-id="element-4684" />

                {/* Boundary Statement */}
                <div className="mt-12 border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-4685">
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4686">
                    This report explains how your system operates. It does not
                    tell you what to do, how to change, or who to become.
                    Implementation of any insights requires individual
                    assessment and, where appropriate, professional support.
                    This is a diagnostic document, not a prescription.
                  </p>
                </div>
              </section>}
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" data-id="element-4687">
          <div className="max-w-7xl mx-auto text-center" data-id="element-4688">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-4689">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}